/**
 * TOWER BIOMES - Multi-floor dungeon variants
 *
 * Towers are special dungeon types featuring:
 * - Multiple floors (3, 5, or 10)
 * - Stairs tiles for ascending/descending
 * - Checkpoint tiles for rest (+50% HP) and safe exit
 * - Boss only on final floor
 * - Floor state preservation (going back up remembers progress)
 */

// ============================================================================
// TOWER BIOME DEFINITIONS
// ============================================================================

export const TOWER_BIOMES = {
  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 1 - 3 FLOORS (Beginner, Era 1+)
  // ═══════════════════════════════════════════════════════════════════════════
  abandonedTower: {
    id: 'abandonedTower',
    name: 'Abandoned Tower',
    icon: '🗼',
    description: 'A crumbling watchtower with three levels of danger. Perfect for aspiring adventurers.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#4a4a5a',
    minAge: 1,
    spawnWeight: 8,  // Same as tier1 dungeon

    // Tower-specific properties
    isTowerBiome: true,
    towerFloors: 3,
    dungeonTier: 'tier1',
    cooldownMinutes: 15,

    // Mob pool: Same as tier1 dungeons
    dungeonMobPool: ['goblin', 'skeleton', 'wolf'],
    dungeonBossPool: ['boss_ogre'],
    dungeonLootTable: 'common',

    // Entry events
    entryEvents: [
      { id: 'crumbling', weight: 30, text: 'Dust falls from above as you enter. The tower groans.' },
      { id: 'stairs_up', weight: 40, text: 'Spiral stairs wind upward into darkness.', rewards: { knowledge: 3 } },
      { id: 'old_camp', weight: 20, text: 'An old campsite suggests others came before you.', rewards: { gold: 5 } },
      { id: 'collapse', weight: 10, text: 'A section of wall collapses!', penalties: { food: 3 } },
    ],

    gatherableResources: {
      stone: { baseAmount: 3, richness: 0.5 },
      knowledge: { baseAmount: 1, richness: 0.3 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 2 - 5 FLOORS (Intermediate, Era 3+)
  // ═══════════════════════════════════════════════════════════════════════════
  mageSpire: {
    id: 'mageSpire',
    name: 'Mage Spire',
    icon: '🔮',
    description: 'A wizard\'s tower crackling with residual magic. Five floors of arcane danger await.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#5a4a6a',
    minAge: 3,
    spawnWeight: 5,  // Same as tier2 dungeon

    // Tower-specific properties
    isTowerBiome: true,
    towerFloors: 5,
    dungeonTier: 'tier2',
    cooldownMinutes: 25,

    // Mob pool: Same as tier2 dungeons
    dungeonMobPool: ['skeleton', 'wraith', 'orc', 'darkKnight'],
    dungeonBossPool: ['boss_lich'],
    dungeonLootTable: 'uncommon',

    // Entry events
    entryEvents: [
      { id: 'arcane_ward', weight: 25, text: 'Magical wards flicker as you pass through.' },
      { id: 'library', weight: 30, text: 'Scattered tomes line the entrance hall.', rewards: { knowledge: 8, magic: 3 } },
      { id: 'enchanted', weight: 25, text: 'Enchantments empower you briefly!', rewards: { magic: 5 } },
      { id: 'spell_trap', weight: 15, text: 'A magical trap activates!', penalties: { food: 6 } },
      { id: 'cursed_book', weight: 5, text: 'A cursed tome drains your energy!', penalties: { food: 10, magic: 2 } },
    ],

    gatherableResources: {
      magic: { baseAmount: 4, richness: 0.7 },
      knowledge: { baseAmount: 3, richness: 0.5 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 3 - 5 FLOORS (Advanced, Era 5+)
  // ═══════════════════════════════════════════════════════════════════════════
  darkCitadel: {
    id: 'darkCitadel',
    name: 'Dark Citadel',
    icon: '🏰',
    description: 'A fortress of shadow with five deadly levels. Only the strong survive.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#3a2a4a',
    minAge: 5,
    spawnWeight: 3,  // Same as tier3 dungeon

    // Tower-specific properties
    isTowerBiome: true,
    towerFloors: 5,
    dungeonTier: 'tier3',
    cooldownMinutes: 40,

    // Mob pool: Same as tier3 dungeons
    dungeonMobPool: ['orc', 'wraith', 'darkKnight', 'demon', 'vampire'],
    dungeonBossPool: ['boss_dragon'],
    dungeonLootTable: 'rare',

    // Entry events
    entryEvents: [
      { id: 'shadow_gate', weight: 20, text: 'Shadows writhe at the citadel entrance.' },
      { id: 'dark_altar', weight: 25, text: 'A dark altar holds forbidden treasures!', rewards: { gold: 15, gems: 1, magic: 6 } },
      { id: 'power_surge', weight: 20, text: 'Dark energy courses through you.', rewards: { magic: 10 }, penalties: { food: 5 } },
      { id: 'gargoyle_attack', weight: 20, text: 'Stone gargoyles animate and attack!', penalties: { food: 12 } },
      { id: 'void_leak', weight: 15, text: 'Void energy seeps from the walls.', penalties: { food: 10, magic: 4 } },
    ],

    gatherableResources: {
      magic: { baseAmount: 5, richness: 0.8 },
      gems: { baseAmount: 1, richness: 0.3 },
      knowledge: { baseAmount: 4, richness: 0.6 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 4 - 10 FLOORS (Endgame, Era 7+)
  // ═══════════════════════════════════════════════════════════════════════════
  voidTower: {
    id: 'voidTower',
    name: 'Void Tower',
    icon: '🌑',
    description: 'Ten floors at the edge of reality. The void hungers for those who dare climb.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#1a0a2e',
    minAge: 7,
    spawnWeight: 2,  // Same as tier4 dungeon

    // Tower-specific properties
    isTowerBiome: true,
    towerFloors: 10,
    dungeonTier: 'tier4',
    cooldownMinutes: 60,

    // Mob pool: Same as tier4 dungeons
    dungeonMobPool: ['voidStalker', 'abyssalKnight', 'shadowLord', 'demon', 'vampire'],
    dungeonBossPool: ['boss_abyssalLord'],
    dungeonLootTable: 'epic',

    // Entry events
    entryEvents: [
      { id: 'void_whispers', weight: 20, text: 'Whispers from the void promise power... and oblivion.' },
      { id: 'void_treasure', weight: 25, text: 'Treasures from beyond reality materialize!', rewards: { gold: 35, magic: 12, gems: 2 } },
      { id: 'reality_bend', weight: 20, text: 'Reality bends around you.', rewards: { knowledge: 20 }, penalties: { food: 15 } },
      { id: 'void_touched', weight: 20, text: 'The void marks your equipment!', penalties: { food: 20, magic: 8 } },
      { id: 'cosmic_gift', weight: 15, text: 'A fragment of cosmic power empowers you!', rewards: { magic: 25, crystals: 1 } },
    ],

    gatherableResources: {
      magic: { baseAmount: 7, richness: 1.0 },
      crystals: { baseAmount: 2, richness: 0.5 },
      artifacts: { baseAmount: 1, richness: 0.2 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 5 - 10 FLOORS (Ultimate, Era 9+)
  // ═══════════════════════════════════════════════════════════════════════════
  eternalSpire: {
    id: 'eternalSpire',
    name: 'Eternal Spire',
    icon: '✨',
    description: 'The ultimate challenge - ten floors of cosmic horror where eternity meets oblivion.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#0a0020',
    minAge: 9,
    spawnWeight: 1,

    // Tower-specific properties
    isTowerBiome: true,
    towerFloors: 10,
    dungeonTier: 'tier5',
    cooldownMinutes: 90,

    // Mob pool: Same as tier5 dungeons
    dungeonMobPool: ['cosmicHorror', 'eternityWarden', 'voidEmperor', 'voidStalker', 'shadowLord'],
    dungeonBossPool: ['boss_voidGod'],
    dungeonLootTable: 'legendary',

    // Entry events
    entryEvents: [
      { id: 'eternity_beckons', weight: 20, text: 'Eternity itself seems to watch your approach.' },
      { id: 'cosmic_treasure', weight: 25, text: 'Treasures from the birth of the universe appear!', rewards: { gold: 70, magic: 25, gems: 4, artifacts: 1 } },
      { id: 'cosmic_knowledge', weight: 20, text: 'Ancient cosmic knowledge floods your mind!', rewards: { knowledge: 40, science: 8 } },
      { id: 'eternity_price', weight: 20, text: 'Eternity demands a price!', penalties: { food: 30, magic: 15 } },
      { id: 'eternal_fragment', weight: 15, text: 'A fragment of eternity empowers you!', rewards: { magic: 40, crystals: 3, artifacts: 1 } },
    ],

    gatherableResources: {
      magic: { baseAmount: 10, richness: 1.2 },
      crystals: { baseAmount: 4, richness: 0.7 },
      artifacts: { baseAmount: 2, richness: 0.4 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all tower biomes that are available at the current age
 * @param {number} age - Current game age/era
 * @returns {Array} Available tower biomes
 */
export function getTowerBiomesByAge(age) {
  return Object.values(TOWER_BIOMES).filter(biome => biome.minAge <= age);
}

/**
 * Check if a biome is a tower biome
 * @param {string} biomeId - The biome ID to check
 * @returns {boolean} True if tower biome
 */
export function isTowerBiome(biomeId) {
  return TOWER_BIOMES[biomeId]?.isTowerBiome === true;
}

/**
 * Get tower configuration for a biome
 * @param {string} biomeId - The biome ID
 * @returns {Object|null} Tower configuration or null if not a tower
 */
export function getTowerConfigForBiome(biomeId) {
  const biome = TOWER_BIOMES[biomeId];
  if (!biome?.isTowerBiome) return null;

  return {
    floors: biome.towerFloors,
    tier: biome.dungeonTier,
    mobPool: biome.dungeonMobPool,
    bossPool: biome.dungeonBossPool,
    lootTable: biome.dungeonLootTable,
    cooldownMinutes: biome.cooldownMinutes,
  };
}

export default TOWER_BIOMES;
